/**
 * Balance & Plans support for the Strata customer page.
 *
 * Ports the base-plan classification and "other balances" derivation logic
 * from the partner-portal reference implementation
 * (`apps/partner-portal/src/features/accounts/totogiBasePlans.ts` and
 * `apps/partner-portal/src/features/accounts/components/AccountBalanceSheet.tsx`)
 * so the two customer-care surfaces agree on what counts as a required base
 * plan vs. a purchasable bundle vs. an unclaimed "other" balance.
 */

export type RawBalanceType = {
  balanceTypeId?: string | null;
  name?: string | null;
  unitType?: string | null;
};

export type RawBalance = {
  balanceId: string;
  balanceType?: RawBalanceType | null;
  from?: string | null;
  to?: string | null;
  total?: number | null;
  reserved?: number | null;
  used?: number | null;
  available?: number | null;
  value?: number | null;
  unlimited?: boolean;
  planSubscriptionId?: string | null;
  balanceName?: string | null;
  unit?: string | null;
};

export type RawActivePlanVersion = {
  planSubscriptionId: string;
  from?: string;
  to?: string;
  renewal?: string | null;
  planVersion?: {
    id: string;
    version?: string;
    plan?: { id: string; name?: string };
  };
};

// ---- Base plan classification ----------------------------------------

export const REQUIRED_BASE_PLAN_NAMES = [
  'Casual Plan',
  'RG902 Data Traffic',
  'USSD Plan',
  'BNumbers',
  'Loyalty Plan',
] as const;

export const SUSPENSION_BASE_PLAN_NAME = 'Account Suspension' as const;

export type BasePlanName =
  | (typeof REQUIRED_BASE_PLAN_NAMES)[number]
  | typeof SUSPENSION_BASE_PLAN_NAME;

export type BasePlanSubscription = {
  id: string;
  planName: BasePlanName;
};

export type BasePlanGroup = {
  name: BasePlanName;
  subscriptions: BasePlanSubscription[];
  count: number;
  isMissing: boolean;
  isDuplicate: boolean;
};

function normalisePlanName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const basePlanNameByKey = new Map<string, BasePlanName>(
  [...REQUIRED_BASE_PLAN_NAMES, SUSPENSION_BASE_PLAN_NAME].map((name) => [
    normalisePlanName(name),
    name,
  ]),
);

export function getBasePlanName(
  name: string | null | undefined,
): BasePlanName | null {
  if (!name) return null;
  return basePlanNameByKey.get(normalisePlanName(name)) ?? null;
}

export function isBasePlanName(name: string | null | undefined): boolean {
  return getBasePlanName(name) !== null;
}

export function createBasePlanGroups(
  activePlanVersions: RawActivePlanVersion[],
  suspended = false,
): BasePlanGroup[] {
  const requiredNames: BasePlanName[] = suspended
    ? [...REQUIRED_BASE_PLAN_NAMES, SUSPENSION_BASE_PLAN_NAME]
    : [...REQUIRED_BASE_PLAN_NAMES];
  const groupsByName = new Map<BasePlanName, BasePlanSubscription[]>(
    requiredNames.map((name) => [name, []]),
  );

  for (const planVersion of activePlanVersions) {
    const id = planVersion.planSubscriptionId?.trim();
    const rawName = planVersion.planVersion?.plan?.name?.trim();
    if (!id || !rawName) continue;

    const basePlanName = getBasePlanName(rawName);
    if (!basePlanName) continue;

    groupsByName.get(basePlanName)?.push({ id, planName: basePlanName });
  }

  return requiredNames.map((name) => {
    const subscriptions = groupsByName.get(name) ?? [];
    return {
      name,
      subscriptions,
      count: subscriptions.length,
      isMissing: subscriptions.length === 0,
      isDuplicate: subscriptions.length > 1,
    };
  });
}

// ---- Other balances (raw balances not backed by a currently-active,
//      currently-rendered plan subscription) ---------------------------

function getBalanceQuantities(balance: RawBalance): {
  available: number;
  reserved: number;
  remaining: number;
} {
  const available = Number.isFinite(balance.available)
    ? Math.max(0, balance.available as number)
    : 0;
  const rawReserved = Number.isFinite(balance.reserved)
    ? Math.max(0, balance.reserved as number)
    : 0;
  const remaining = Number.isFinite(balance.value)
    ? Math.max(0, (balance.value as number) ?? 0)
    : available + rawReserved;
  const reserved = Math.min(rawReserved, remaining);

  return {
    available: Math.max(0, remaining - reserved),
    reserved,
    remaining,
  };
}

function getBalanceTotal(balance: RawBalance): number {
  return Number.isFinite(balance.total) ? Math.max(0, balance.total as number) : 0;
}

function isBalanceCurrent(balance: RawBalance): boolean {
  if (!balance.to) return true;
  const expiresAt = Date.parse(balance.to);
  return Number.isNaN(expiresAt) || expiresAt > Date.now();
}

function isApplicableRawBalance(balance: RawBalance): boolean {
  if (!isBalanceCurrent(balance)) return false;
  if (balance.unlimited) return true;
  return getBalanceQuantities(balance).remaining > 0;
}

function isCurrencyBalance(balance: RawBalance): boolean {
  const balanceTypeId = balance.balanceType?.balanceTypeId?.toLowerCase();
  const balanceTypeName = balance.balanceType?.name?.toLowerCase();
  return balanceTypeId === 'monetary' || balanceTypeName === 'monetary';
}

export function getBalanceLabel(balance: RawBalance): string {
  return (
    balance.balanceName?.trim() ||
    balance.balanceType?.name?.trim() ||
    balance.balanceType?.balanceTypeId?.trim() ||
    balance.balanceId
  );
}

function formatNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.?0+$/, '');
}

function formatDataAmount(value: number): string {
  const mb = value / 1_000_000;
  if (mb >= 1000) return `${formatNumber(Number((mb / 1000).toFixed(2)))} GB`;
  return `${Math.round(mb)} MB`;
}

function formatCurrencyAmount(value: number): string {
  return `R${value.toFixed(2)}`;
}

export function formatOtherBalanceUsage(balance: RawBalance): string {
  if (balance.unlimited) return 'Unlimited';

  const { remaining } = getBalanceQuantities(balance);
  const total = getBalanceTotal(balance);
  const unitType = balance.balanceType?.unitType;
  const unit = balance.unit ?? unitType ?? '';

  if (isCurrencyBalance(balance)) {
    return `${formatCurrencyAmount(remaining)} of ${formatCurrencyAmount(total)} left`;
  }
  if (unitType === 'VOLUME') {
    return `${formatDataAmount(remaining)} of ${formatDataAmount(total)} left`;
  }
  if (unitType === 'TIME') {
    return `${Math.floor(remaining / 60)} min of ${Math.floor(total / 60)} min left`;
  }
  if (unitType === 'CENTS' || unit.toLowerCase() === 'cents') {
    return `R${remaining.toFixed(2)} of R${total.toFixed(2)} left`;
  }

  const suffix = unit ? ` ${unit}` : '';
  if (total > 0) {
    return `${formatNumber(remaining)}${suffix} of ${formatNumber(total)}${suffix} left`;
  }
  return `${formatNumber(remaining)}${suffix} left`;
}

export function formatOtherBalanceExpiry(balance: RawBalance): string {
  if (!balance.to) return 'No expiry';

  const expiresAt = Date.parse(balance.to);
  if (Number.isNaN(expiresAt)) return 'No expiry';

  const remainingSeconds = Math.floor((expiresAt - Date.now()) / 1000);
  if (remainingSeconds <= 0) return 'Expired';

  const remainingDays = Math.floor(remainingSeconds / 86400);
  const remainingHours = Math.floor((remainingSeconds % 86400) / 3600);
  const dayPart =
    remainingDays > 0 ? `${remainingDays} day${remainingDays === 1 ? '' : 's'}` : '';
  const hourPart =
    remainingHours > 0 ? `${remainingHours} hour${remainingHours === 1 ? '' : 's'}` : '';

  if (dayPart && hourPart) return `${dayPart} and ${hourPart}`;
  if (dayPart) return dayPart;
  if (hourPart) return hourPart;
  return 'Less than 1 hour';
}

export function getOtherBalanceSegments(balance: RawBalance): {
  remainingPercent: number;
  availablePercent: number;
  reservedPercent: number;
  usedPercent: number;
} {
  const { available, reserved, remaining } = getBalanceQuantities(balance);
  const total = getBalanceTotal(balance);
  const computePercent = (value: number) => {
    if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(value)) return 0;
    return Math.round(Math.min(100, Math.max(0, (value / total) * 100)));
  };
  const remainingPercent = balance.unlimited ? 100 : computePercent(remaining);

  return {
    remainingPercent,
    availablePercent: balance.unlimited ? 100 : computePercent(available),
    reservedPercent: balance.unlimited ? 0 : computePercent(reserved),
    usedPercent: balance.unlimited ? 0 : Math.max(0, 100 - remainingPercent),
  };
}

/**
 * Raw balances that aren't currently represented by any rendered bundle
 * (ordinary or base-plan-attached). A balance is "represented" when its
 * `planSubscriptionId` matches a bundle already being shown — which only
 * happens when that plan subscription is still active. Balances whose
 * owning plan subscription has since been archived/cancelled (but which
 * still carry real remaining value) fall through here, matching the
 * partner-portal reference behaviour.
 */
export function getOtherApplicableBalances({
  balances,
  excludePlanSubscriptionIds,
}: {
  balances: RawBalance[];
  excludePlanSubscriptionIds: Set<string>;
}): RawBalance[] {
  return balances.filter((balance) => {
    if (!isApplicableRawBalance(balance)) return false;
    const planSubscriptionId = balance.planSubscriptionId?.trim();
    if (planSubscriptionId && excludePlanSubscriptionIds.has(planSubscriptionId)) {
      return false;
    }
    return true;
  });
}
